import { useEffect, useRef } from 'react';
import { api } from 'aws-blocks';

export type RoomSubscription = {
  roomId: string;
  mode: 'poll' | 'sse' | 'none';
  pollIntervalMs?: number;
  url?: string;
};

export type UseRoomSubscriptionOptions<T> = {
  roomId: string | undefined;
  enabled?: boolean;
  onEvent: (payload: T) => void;
  /** Optional initial fetch already done; poll still runs. */
  fetchSnapshot?: () => Promise<T | null | undefined>;
};

/**
 * Visibility-aware room updates.
 * Poll when subscribeToGame returns mode `poll`; skip when `none`.
 */
export default function useRoomSubscription<T = unknown>({
  roomId,
  enabled = true,
  onEvent,
  fetchSnapshot,
}: UseRoomSubscriptionOptions<T>) {
  const onEventRef = useRef(onEvent);
  const fetchRef = useRef(fetchSnapshot);
  onEventRef.current = onEvent;
  fetchRef.current = fetchSnapshot;

  useEffect(() => {
    if (!roomId || !enabled) return;

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;
    let eventSource: EventSource | undefined;

    const pollOnce = async () => {
      if (cancelled || document.visibilityState === 'hidden') return;
      try {
        const fetchFn =
          fetchRef.current ??
          (async () => (await api.getGame(roomId)) as T | null);
        const snapshot = await fetchFn();
        if (!cancelled && snapshot && !(snapshot as { unchanged?: boolean }).unchanged) {
          onEventRef.current(snapshot);
        }
      } catch {
        // transient network errors — next tick retries
      }
    };

    const startPoll = (ms: number) => {
      void pollOnce();
      intervalId = setInterval(() => void pollOnce(), ms);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void pollOnce();
    };

    (async () => {
      try {
        const sub = (await api.subscribeToGame(roomId)) as RoomSubscription;
        if (cancelled) return;

        if (sub.mode === 'sse' && sub.url) {
          eventSource = new EventSource(sub.url, { withCredentials: true });
          eventSource.onmessage = (ev) => {
            try {
              onEventRef.current(JSON.parse(ev.data) as T);
            } catch {
              // ignore malformed
            }
          };
          return;
        }

        const ms = Number(sub.pollIntervalMs);
        if (sub.mode === 'none' || !(ms > 0)) {
          return;
        }

        startPoll(ms);
        document.addEventListener('visibilitychange', onVisibility);
      } catch {
        // Do not fall back to anonymous getGame — AuthZ requires a seated player.
        if (!cancelled) {
          // leave polling off when subscribe fails (auth / membership)
        }
      }
    })();

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      eventSource?.close();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [roomId, enabled]);
}
